import React from "react";
import FormattedMessage from "components/FormattedMessage";
import { Box, Flex, Text, H2, Image } from "ui-components";
import { cardStyles, containerStyles } from "./styles";
import map from "lodash/map";
import get from "lodash/get";
import { createStructuredSelector } from "../../../../node_modules/reselect/es/index";
import { connect, useDispatch } from "react-redux";
import { makeSelectIsSimTypeEsim } from "ducks/selectors/planService";
import { clearData, setData } from "ducks/actions/index";
import ReactTooltip from "react-tooltip";
import { InformationOutline } from "components/Icons";

const SwitchContainer = (props) => {
  return (
    <Box sx={containerStyles} data-testid="default-container">
      {props.children}
    </Box>
  );
};

const Tab = (props) => {
  const { item, selected, onClick, icons, valueAttribute } = props;

  const itemKey = get(item, "name") || get(item, valueAttribute) || get(item, "value") || "";
  const labelText = get(item, "label") || "";
  const isEsim =
    String(itemKey).toLowerCase().includes("e-sim") ||
    String(labelText).toLowerCase().includes("e_sim");

  // Badge handling: default eSIM badge to "Recommended"
  const badgeText = isEsim ? "Recommended" : null;

  // Subtitle handling: default eSIM subtitle to "Activate faster within 5 minutes"
  const subtitle =
    get(item, "subTitle") ||
    item.description ||
    item.subtitle ||
    (isEsim
      ? "Activate faster within 5 minutes"
      : "Delivered to you in 3-5 working days.");

  const iconImageUrl = selected
    ? get(icons, `${itemKey}.imageUrl`)
    : get(icons, `${itemKey}.inactiveImageUrl`) ||
      get(icons, `${itemKey}.imageUrl`) ||
      item.icon;

  const onTabClick = () => onClick(item);

  return (
    <Box
      sx={cardStyles(selected)}
      data-testid={`toggle-${get(item, valueAttribute)}`}
      onClick={onTabClick}
    >
      <Flex alignItems="center" width="100%" justifyContent="space-between">
        {/* Left Section: Icon + Inline Title/Badge + Subtitle */}
        <Flex alignItems="center" gap="14px" flex="1">
          {/* Icon Box */}
          <Flex
            alignItems="center"
            justifyContent="center"
            sx={{
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              backgroundColor: "#f1f5f9",
              flexShrink: 0,
              mr: "12px"
            }}
          >
            {iconImageUrl ? (
              <Image src={iconImageUrl} sx={{ width: "24px", height: "24px" }} />
            ) : isEsim ? (
              /* eSIM Microchip Icon (Figma Match) */
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#64748b"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="4" y="4" width="16" height="16" rx="3" />
                <rect x="8" y="8" width="8" height="8" rx="1" />
                <line x1="10" y1="4" x2="10" y2="6" />
                <line x1="14" y1="4" x2="14" y2="6" />
                <line x1="10" y1="18" x2="10" y2="20" />
                <line x1="14" y1="18" x2="14" y2="20" />
                <line x1="4" y1="10" x2="6" y2="10" />
                <line x1="4" y1="14" x2="6" y2="14" />
                <line x1="18" y1="10" x2="20" y2="10" />
                <line x1="18" y1="14" x2="20" y2="14" />
              </svg>
            ) : (
              /* Physical SIM Icon */
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#64748b"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 2h8l5 5v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" />
                <rect x="9" y="11" width="6" height="6" rx="1" />
              </svg>
            )}
          </Flex>

          {/* Text Container */}
          <Box flex="1">
            {/* Inline Title + Badge Row */}
            <Flex alignItems="center" gap="8px" mb="4px" flexWrap="nowrap">
              <FormattedMessage id={item.label || "unknown"} values={item}>
                {(msg) => (
                  <Text
                    color="#0f172a"
                    fontWeight="700"
                    fontSize="18px"
                    lineHeight="1"
                  >
                    {msg}
                  </Text>
                )}
              </FormattedMessage>

              {badgeText && (
                <Box
                  sx={{
                    backgroundColor: "#e0f2fe",
                    color: "#0284c7",
                    fontSize: "13px",
                    fontWeight: "600",
                    px: "8px",
                    py: "4px",
                    borderRadius: "6px",
                    lineHeight: "1",
                    whiteSpace: "nowrap"
                  }}
                >
                  {typeof badgeText === "string" ? (
                    <FormattedMessage id={badgeText} defaultMessage={badgeText} />
                  ) : (
                    badgeText
                  )}
                </Box>
              )}
            </Flex>

            {/* Subtitle Row */}
            {subtitle && (
              <Text fontSize="14px" color="#64748b" lineHeight="1.3">
                {subtitle}
              </Text>
            )}
          </Box>
        </Flex>

        {/* Right Section: Radio Button Indicator */}
        <Flex
          alignItems="center"
          justifyContent="center"
          sx={{
            width: "24px",
            height: "24px",
            borderRadius: "50%",
            border: selected ? "2.5px solid #0f172a" : "2px solid #cbd5e1",
            flexShrink: 0,
            ml: "16px"
          }}
        >
          {selected && (
            <Box
              sx={{
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                backgroundColor: "#0f172a"
              }}
            />
          )}
        </Flex>
      </Flex>
    </Box>
  );
};

const ToggleSwitch = (props) => {
  const {
    items = [],
    value,
    valueAttribute = "value",
    onClick,
    badges,
    icons,
    shouldHide,
    isSimTypeEsim
  } = props;
  const title = "Select your SIM type";
  const dispatch = useDispatch();
  
  const handleTabClick = (item) => {
    const itemValue = get(item, valueAttribute);
    if (value && itemValue !== value) {
      dispatch(
        setData({
          key: "simCount",
          data: {
            newSimCount: 0,
            transferSimCount: 0,
            totalSimCount: 0
          }
        })
      );
    }
    onClick(item);
  };



  if (shouldHide) return null;

  return (
    <Box mb={4}>
      {/* Title Header */}
      {title && (
        <Flex alignItems="center" mb="24px">
          <style>{`
            .custom-sim-tooltip {
              border-radius: 12px !important;
              box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
              padding: 12px 16px !important;
              max-width: 280px !important;
              font-size: 14px !important;
              line-height: 1.4 !important;
              font-weight: 500 !important;
              text-align: left !important;
              white-space: normal !important;
              opacity: 1 !important;
            }
          `}</style>
          <H2 data-testid="toggle_switch_title" mb="0px" fontSize="22px" fontWeight="700" color="#0f172a">
            <FormattedMessage id={title} />
          </H2>
          <Box
            ml="8px"
            data-tip="You can only select one SIM type per order. If you need another SIM type, you can place a separate order later."
            data-for="sim-type-tooltip"
            sx={{
              display: "flex",
              cursor: "pointer"
            }}
          >
            <InformationOutline color="#64748b" size="24px" />
          </Box>
          <ReactTooltip
            id="sim-type-tooltip"
            place="bottom"
            type="light"
            effect="solid"
            border={true}
            borderColor="#e2e8f0"
            textColor="#64748b"
            backgroundColor="#ffffff"
            className="custom-sim-tooltip"
          />
        </Flex>
      )}

      {/* Grid Cards */}
      <SwitchContainer>
        {map(items, (item) => {
          const selected = get(item, valueAttribute) === value;
          return (
            <Tab
              key={get(item, valueAttribute) || item.value}
              item={item}
              selected={selected}
              onClick={handleTabClick}
              badges={badges}
              icons={icons}
              valueAttribute={valueAttribute}
            />
          );
        })}
      </SwitchContainer>

      {/* Footer Compatibility Link */}
      {isSimTypeEsim === true ? (
        <Flex alignItems="center" gap="6px" mt={3} color="#64748b" fontSize="15px">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#64748b"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <Text color="#64748b" fontSize="15px">
              <>
                Check if your phone is compatible with eSIM{" "}
                <a
                  href="https://www.circles.life/sg/esim-compatibility-checker/"
                  style={{ color: "#0088ff", textDecoration: "underline", fontWeight: 600 }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </a>
                .
              </>
          </Text>
        </Flex>
      ) : null}
    </Box>
  );
};

ToggleSwitch.defaultProps = {
  items: []
};

export function mapDispatchToProps(dispatch) {
  return {
    resetSimCount: () => {
      dispatch(clearData("simCount"));
    }
  };
}

const mapStateToProps = createStructuredSelector({
  isSimTypeEsim: makeSelectIsSimTypeEsim()
});

export default connect(mapStateToProps,mapDispatchToProps)(ToggleSwitch);
